import { authApi } from '@mathflat/domain/Auth'
import {
  type TeacherActivityParams,
  teacherApi,
  type TeacherId,
  type TeacherListParams,
} from '@mathflat/domain/Teacher'
import {
  DIFFICULTY_LEVEL,
  teacherSettingApi,
  type TeacherSettingBody,
} from '@mathflat/domain/TeacherSetting'
import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'
import ExtendedMap from 'src/models/ExtendedMap'

import { MAX_REQUEST_NUMBER } from '@/constant/common'
import { LevelSetting, LevelSettingColl, Teacher, User } from '@/dto'
import { ReqParams } from '@/dto/common/ReqParams'

import queryClient from './queryClient'

class TeacherController {
  fetchHasTeachers = async () => {
    const { content } = await teacherApi.getTeachers(
      { size: 1, page: 0 },
      { mp_event_name: 'T51-10_선생님조회_API' },
    )
    return content.length > 0
  }

  fetchTeachers = async (params: TeacherListParams) => {
    const { content, last } = await teacherApi.getTeachers(params, {
      mp_event_name: 'T51-10_선생님조회_API',
    })

    return {
      teacherColl: new CheckableColl(
        CheckableMap.fromArr(
          content.map((teacher) => new Teacher(teacher)),
          'teacherId' as keyof Teacher,
        ),
      ),
      last,
    }
  }

  // 전체 선생님 중
  fetchAllTeachers = () => {
    return queryClient.fetchQuery({
      queryKey: ['fetchAllTeachers'],
      queryFn: () =>
        this.fetchTeachers(
          new ReqParams({ params: { size: MAX_REQUEST_NUMBER, present: true } }).toRequestParams,
        ),
    })
  }

  fetchUserTeacher = async (teacherId: TeacherId) => {
    const teacher = await teacherApi.getTeacher(teacherId, {
      mp_event_name: 'T51-12_특정선생님조회_API',
    })
    return new User({ teacher })
  }

  private fetchSetting = () =>
    teacherSettingApi.getTeacherSetting({
      mp_event_name: 'T99-01_학습지만들기1단계_난이도설정(무조건발생)_API',
    })

  fetchTeacherSetting = async () => {
    const setting = await this.fetchSetting()
    const levelSettings = DIFFICULTY_LEVEL.map(
      (key) => new LevelSetting(key, setting[key].difficultySetting),
    )

    return {
      levelSetting: new LevelSettingColl(ExtendedMap.fromArr(levelSettings, 'key')),
      worksheetSearchSetting: setting.worksheetSearchSetting,
      workbookSearchSetting: setting.workbookSearchSetting,
      labSetting: setting.labSetting,
      quickMenuSetting: setting.quickMenuSetting,
    }
  }

  fetchTeacherWorksheetFilterSetting = () => this.fetchSetting()

  fetchTeacherLabSetting = () => this.fetchSetting()

  updateTeacherSetting = async (body: TeacherSettingBody) => {
    await teacherSettingApi.updateTeacherSetting(body, {
      mp_event_name: 'T35-02_학습지만들기1단계_난이도설정수정_API',
    })
  }

  createTeacher = async (teacher: Teacher) => {
    const { name, phone, loginId, type } = teacher
    await teacherApi.createTeacher(
      // 등록 화면에 비밀번호 입력칸이 없어 로그인 아이디를 처음 비밀번호로 보낸다
      {
        name,
        phone,
        loginId,
        type,
        password: loginId,
      },
      { mp_event_name: 'T51-11_선생님등록_API' },
    )
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  modifyTeacher = async (teacher: Teacher) => {
    const { teacherId, name, phone, type } = teacher
    await teacherApi.updateTeacher(
      teacherId,
      { name, phone, type },
      { mp_event_name: 'T51-12_특정선생님수정_API' },
    )
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  deleteTeachers = async (teacherIds: TeacherId[]) => {
    await teacherApi.deleteTeachers(teacherIds, { mp_event_name: 'T51-10_여러선생님삭제_API' })
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  initPassword = async ({ teacherId, loginId }: Teacher) => {
    await authApi.resetPassword(
      {
        relationId: teacherId,
        type: 'TEACHER',
        newPassword: loginId,
        newPasswordCheck: loginId,
      },
      { mp_event_name: 'T51-04_학생학부모비번수정_API' },
    )
  }

  fetchActivityCount = (params: TeacherActivityParams) => teacherApi.getTeacherActivityCount(params)
}

export const teacherController = new TeacherController()
